import { useEffect, useRef } from "react";

/**
 * In-app confirmation, replacing `window.confirm()` for a consequential
 * action (issue #166). Chromium silently auto-suppresses `confirm()`/
 * `alert()` after several have appeared in the same tab, resolving them to
 * `false` with zero visible feedback — a suppressed dialog was
 * indistinguishable from a real bug. A plain React-rendered overlay is
 * immune to that browser-level suppression, and — unlike a native
 * `<dialog>`/`showModal()` — needs no `showModal` support in this project's
 * jsdom 25.0.1 test toolchain (see `features/campaigns/consequentialActions.ts`).
 *
 * Codex review on PR #168: keyboard-complete per design.md's own
 * accessibility acceptance ("keyboard-complete dialogs/drawers", "focus
 * restoration") — same Escape + boundary Tab-trap pattern already
 * established for the mobile nav drawer (`app/AppShell.tsx`), plus restoring
 * focus to whatever triggered the dialog on close. Both actions carry the
 * shared `.v2-u-target-min` 44px utility (design.md's "44×44 px targets",
 * already used by every other interactive control in the shell), and the
 * actions row wraps at narrow/zoomed widths instead of overflowing
 * (design.md's 200% zoom / no-horizontal-overflow acceptance).
 */
export function ConfirmDialog({
  message,
  onConfirm,
  onCancel,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
}: {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** panel-redesign-v3 T4 (REDISENO-PANEL.md §5.2): a consequential action may
   * need copy more specific than generic "Confirmar"/"Cancelar" (e.g. the
   * discard-campaign dialog's "Descartar"/"Seguir editando") — optional and
   * backward compatible, every existing caller keeps the defaults. */
  confirmLabel?: string;
  cancelLabel?: string;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    confirmButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel();
        return;
      }

      if (event.key === "Tab" && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0]!;
        const last = focusable[focusable.length - 1]!;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus();
    };
    // onCancel is provided fresh by the caller on every render (an inline
    // closure over `pendingAction`) — re-running this effect on every one of
    // those renders would move focus back to Confirmar each time, fighting
    // the operator. Only (re)bind when the dialog's own identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="v2-confirm-dialog-backdrop">
      <div
        ref={dialogRef}
        className="v2-confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-label="Confirmar acción"
        aria-describedby="v2-confirm-dialog-message"
      >
        <p id="v2-confirm-dialog-message">{message}</p>
        <div className="v2-confirm-dialog__actions">
          <button type="button" className="v2-u-target-min v2-confirm-dialog__cancel" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className="v2-u-target-min v2-confirm-dialog__confirm"
            onClick={onConfirm}
            ref={confirmButtonRef}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
